import React, { useEffect, useMemo } from 'react'
import Big from 'big.js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons'

import MoneyField from 'src/components/MoneyField'
import { PLEDGE_MAX } from 'src/components/Validation'

import BackLink from '../BackLink'
import Contact from '../Contact'
import DataSharing from '../DataSharing'
import DonationExtras from '../DonationExtras'
import EmployerMatchingStatus from '../EmployerMatchingStatus'
import PageHeader from '../PageHeader'
import SandwichField from '../SandwichField'
import useContactValidation from '../useContactValidation'
import useValidation from '../useValidation'
import { supportsFancySelect } from '../supportsFancySelect'

// Unified crypto step: dual amount inputs + contact info + extras on
// a single page, then the QR / status page after submit (matches v6's
// two-page model). The two amount inputs always stack vertically
// (fiat on top, crypto below). An earlier iteration laid them
// side-by-side on @roomy with the pickers on the outer edges, but
// some cryptocurrencies' ticker / label widths broke the layout at
// common container sizes — always-stacked turned out simpler and
// more reliable.
//
// Submit flow runs amount validation + the shared contact validation
// (useContactValidation, which also gates AddressElement completeness),
// then fires the two-step backend call (saveDraft + crypto_donations
// POST) via crypto.sendPayment. On success the App routes to
// crypto_info.
const CryptoForm = ({
  i18n, donation, plugins, crypto, currencies, accounts, min_amount,
  session, collect_phone, collect_address, data_sharing,
  beneficiary, partner, partner_is_pledge, foundation, defaultShares,
  extras, blank_modal_src, emoji_picker_loader_src, widget_instance_token,
  employerMatching, onBack, onContinue,
}) => {
  const { state, fiatCurrency } = crypto

  const minAmount = useMemo(
    () => Big(min_amount).times(fiatCurrency.rate).round(fiatCurrency.exponent, 3).toString(),
    [ min_amount, fiatCurrency ],
  )

  const amountValidation = useValidation({
    i18n,
    currency: fiatCurrency,
    fieldSet: {
      fiat_amount:   { required: true, min: minAmount, max: PLEDGE_MAX },
      crypto_amount: { required: true },
    },
  })

  // Parent `donation` reflects only Start-page values per the one-way
  // sync in useCrypto — Contact's `collectAddress` $5000 trigger would
  // miss any threshold crossing the donor reaches by typing on the
  // crypto step. The wrapper overrides amount / currency / usdAmount /
  // collectAddress with crypto-local values while passing dispatch +
  // every other field through, so Contact + useContactValidation see
  // the donor's actual crypto-step intent. The standalone Contact
  // step (fiat path) keeps using the parent `donation` directly.
  const cryptoUsdAmount = useMemo(
    () => Big(state.fiat_amount || 0).div(fiatCurrency.rate),
    [ state.fiat_amount, fiatCurrency.rate ],
  )
  const cryptoDonation = useMemo(() => ({
    ...donation,
    state: { ...donation.state, amount: state.fiat_amount },
    currency: fiatCurrency,
    usdAmount: cryptoUsdAmount,
    // donation.collectAddress already ORs together the partner setting
    // + Canadian receipt + Gift Aid value; we just OR in our own
    // amount-based threshold on top.
    collectAddress: donation.collectAddress || cryptoUsdAmount.gte(5000),
  }), [ donation, state.fiat_amount, fiatCurrency, cryptoUsdAmount ])

  const { validation: contactValidation, validate: validateContact } =
    useContactValidation({ i18n, donation: cryptoDonation, session, collect_phone })

  useEffect(() => {
    amountValidation.resetErrorsFor('fiat_amount')
    amountValidation.resetErrorsFor('crypto_amount')
  }, [ state.fiat_amount, state.crypto_amount ])

  const handleSubmit = async e => {
    e.preventDefault()
    // Run BOTH validations regardless of order so all errors surface in
    // one pass — bailing on the first failure would hide downstream
    // errors until the donor fixes the first one.
    const amountOk = amountValidation.validate(state)
    const contactOk = await validateContact()
    const pluginsOk = plugins.validateSlots([ 'afterDataSharing' ], { donation })
    if (!amountOk || !contactOk || !pluginsOk) return
    const result = await crypto.sendPayment()
    if (!result.errors) onContinue()
  }

  const amountError = amountValidation.errors.fiat_amount?.[0]
    || amountValidation.errors.crypto_amount?.[0]

  // Flatten contact errors to single strings for InputField.
  const contactErrors = Object.fromEntries(
    Object.entries(contactValidation.errors).map(([ k, v ]) => [ k, v?.[0] ])
  )

  // Inline currency symbol via SandwichField — mirrors Start.jsx.
  // `i18n.currency.format` is a template like "%u%n" / "%n %u";
  // strip %c (iso code shows in the picker) and split on %n.
  const format = (i18n.currency?.format || '%u%n').replace('%c', '').trim()
  const [ prefixTemplate = '', suffixTemplate = '' ] = format.split('%n')
  const prefixText = prefixTemplate.replace('%u', fiatCurrency.symbol || '')
  const suffixText = suffixTemplate.replace('%u', fiatCurrency.symbol || '')

  const fiatPicker = currencies.length > 1 ? (
    <select
      aria-label={i18n.t('currency')}
      value={state.fiat_currency}
      onChange={e => crypto.setFiatCurrency(e.target.value)}
      className="form-select form-select-sm input-group-island w-auto field-sizing-content text-end custom-select"
    >
      <button><selectedcontent /></button>
      {currencies.map(c => (
        <option key={c.id} value={c.iso_code} title={c.name}>
          {c.iso_code}
          {supportsFancySelect && (
            <span className="hidden"> — {c.name}</span>
          )}
        </option>
      ))}
    </select>
  ) : (
    <span aria-hidden="true" className="self-center px-3 text-sm text-gray-500">
      {state.fiat_currency}
    </span>
  )

  const cryptoPicker = (
    <select
      aria-label={i18n.t('payment_methods.crypto')}
      value={state.crypto_currency}
      onChange={e => crypto.setCryptoCurrency(e.target.value)}
      className="form-select form-select-sm input-group-island w-auto field-sizing-content text-end custom-select"
    >
      <button><selectedcontent /></button>
      {accounts.map(a => (
        <option key={a.currency} value={a.currency} title={a.name}>
          {a.currency}
          {supportsFancySelect && (
            <span className="hidden"> — {a.name}</span>
          )}
        </option>
      ))}
    </select>
  )

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <PageHeader heading={i18n.t('steps.contact.heading')} />

      <div>
        <div className="flex flex-col items-stretch">
          {/* Fiat input on top, crypto input below (always stacked —
              see the file-top comment for why side-by-side was
              abandoned). The rounded-*-none classes flatten the inner
              edges where the two input-groups meet so they read as a
              single visual unit, with the absolute-positioned ≈
              separator straddling the boundary. */}
          <div className="flex-1">
            <label htmlFor="crypto_fiat_amount" className="sr-only">
              {i18n.t('amount')}
            </label>
            <div className="input-group relative focus-within:z-10 rounded-b-none">
              <SandwichField
                type={MoneyField}
                id="crypto_fiat_amount"
                className="form-control form-control-lg input-group-control rounded-b-none rounded-e-none"
                containerClassName="relative flex-1 min-w-0"
                exponent={fiatCurrency.exponent}
                decimal={i18n.currency?.separator}
                value={state.fiat_amount?.toString() || ''}
                onChange={crypto.handleFiatAmountChange}
                aria-invalid={amountError ? 'true' : undefined}
                inputMode="decimal"
                required
                prefix={prefixText && <span className="text-gray-500"> {prefixText} </span>}
                suffix={suffixText && <span className="text-gray-500"> {suffixText} </span>}
              />
              {fiatPicker}
            </div>
          </div>

          <div
            className="relative z-20 -my-px pointer-events-none"
            aria-hidden="true"
          >
            <div className="rounded-full w-6 h-6 flex items-center justify-center [border-width:var(--input-border-width)] border-(--input-border) bg-white absolute -translate-1/2 top-1/2 left-1/2">
              ≈
            </div>
          </div>

          {/* Crypto input */}
          <div className="flex-1">
            <label htmlFor="crypto_crypto_amount" className="sr-only">
              {i18n.t('payment_methods.crypto')}
            </label>
            <div className="input-group relative focus-within:z-10 rounded-t-none">
              <MoneyField
                id="crypto_crypto_amount"
                className="form-control form-control-lg input-group-control rounded-t-none rounded-e-none flex-1 min-w-0"
                exponent={8}
                decimal={i18n.currency?.separator}
                value={state.crypto_amount?.toString() || ''}
                onChange={crypto.handleCryptoAmountChange}
                aria-invalid={amountError ? 'true' : undefined}
                inputMode="decimal"
                required
              />
              {cryptoPicker}
            </div>
          </div>
        </div>

        {amountError && (
          <p role="alert" className="mt-1 text-sm text-danger">{amountError}</p>
        )}
      </div>

      <Contact
        i18n={i18n}
        donation={cryptoDonation}
        errors={contactErrors}
        resetError={contactValidation.resetErrorsFor}
        collect_phone={collect_phone}
        session={session}
      />

      <div className="space-y-2">
        <DataSharing
          i18n={i18n}
          foundation={foundation}
          partner_is_pledge={partner_is_pledge}
          data_sharing={data_sharing}
          collect_address={collect_address}
          collect_phone={collect_phone}
          beneficiary={beneficiary}
          partner={partner}
          defaultShares={defaultShares}
          shares={donation.shares}
          setShares={donation.setShares}
        />
        <EmployerMatchingStatus
          i18n={i18n}
          employerMatching={employerMatching}
          shares={donation.shares}
        />
        {plugins.renderSlot('afterDataSharing', { donation })}
        <DonationExtras
          extras={extras}
          i18n={i18n}
          blank_modal_src={blank_modal_src}
          emoji_picker_loader_src={emoji_picker_loader_src}
          widget_instance_token={widget_instance_token}
        />
      </div>

      {/* sendPayment surfaces backend errors via crypto.errors. Field
          errors should have been caught client-side; this is the
          base-error catch-all (Coinbase issues, server validation we
          missed, network failures). */}
      {crypto.errors?.base?.[0] && (
        <p
          role="alert"
          className="rounded border border-danger-300 bg-danger-50 text-danger-800 p-3 text-sm"
        >
          {crypto.errors.base[0]}
        </p>
      )}

      <div className="space-y-2">
        <button
          type="submit"
          // Disabled while the quote is refreshing after a currency
          // change (or when that refresh failed) — the shown
          // crypto_amount reflects the previous pair's rate until a
          // fresh quote for the selected pair lands.
          disabled={crypto.rateStale}
          className="btn btn-primary flex w-full items-center justify-center gap-2"
        >
          {i18n.t('continue')}
          <FontAwesomeIcon icon={faArrowRight} className="rtl:-scale-x-100" />
        </button>
        <BackLink i18n={i18n} onClick={onBack} />
      </div>
    </form>
  )
}

export default CryptoForm
